import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ErrorState, Loading } from "@/components/ui";
import { Composer, ThreadMessages } from "@/components/messaging/Thread";
import { useBoutiqueActivity } from "@/layouts/MobileShell";
import { useSession } from "@/hooks/useSession";
import { useSelectedBoutique } from "@/hooks/useSelectedBoutique";
import { useUi } from "@/hooks/useUi";
import { useBoutiqueConversationId } from "@/lib/queries";

/**
 * Messages mobile : fil de la boutique choisie + barre de réponse fixe au-dessus
 * des onglets. Raccourcis vers les autres boutiques qui ont des non-lus.
 */
export function MobileMessages() {
  const { can } = useSession();
  const { selectedId, select } = useSelectedBoutique();
  const { openCompose } = useUi();
  const { conversations } = useBoutiqueActivity();
  const [params, setParams] = useSearchParams();
  const conv = useBoutiqueConversationId(selectedId ?? undefined);
  const id = conv.data?.id;
  const elsewhere = conversations.filter((c) => c.unread > 0 && c.boutique.id !== selectedId);

  // Lien de notification « ?conversation=<id> » : on sélectionne la boutique du fil.
  const convParam = params.get("conversation");
  const target = conversations.find((c) => c.id === convParam);
  useEffect(() => {
    if (!convParam || (!target && conversations.length === 0)) return;
    if (target) select(target.boutique.id);
    const next = new URLSearchParams(params);
    next.delete("conversation");
    setParams(next, { replace: true });
  }, [convParam, target, conversations.length, params, select, setParams]);

  return (
    <div className="flex flex-col gap-3">
      {(can("messages.send") || elsewhere.length > 0) && (
        <div className="flex items-center gap-2">
          <div className="no-scrollbar flex min-w-0 flex-1 gap-2 overflow-x-auto">
            {elsewhere.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => select(c.boutique.id)}
                className="flex min-h-[32px] flex-shrink-0 items-center gap-[6px] whitespace-nowrap rounded-full bg-ios-fill px-[14px] text-[15px] font-medium"
              >
                {c.boutique.city}
                <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-ios-badge px-[5px] text-[12px] font-semibold text-white">
                  {c.unread}
                </span>
              </button>
            ))}
          </div>
          {can("messages.send") && selectedId && (
            <button
              type="button"
              onClick={() => openCompose({ kind: can("tasks.create") ? "tache" : "message", boutiqueIds: [selectedId] })}
              className="min-h-[32px] flex-shrink-0 rounded-full bg-accent px-[14px] text-[15px] font-semibold text-white active:bg-accent-strong"
            >
              + Nouveau
            </button>
          )}
        </div>
      )}
      {conv.isLoading && <Loading />}
      {conv.error && <ErrorState error={conv.error} />}
      {id && <ThreadMessages conversationId={id} compact />}
      {/* Réserve la hauteur de la barre de réponse pour que le dernier message reste visible. */}
      <div className="h-[64px] flex-shrink-0" />
      {id && (
        <div className="fixed inset-x-3 bottom-[calc(max(10px,env(safe-area-inset-bottom))+70px)] z-[24] mx-auto max-w-[456px] rounded-[24px] border-[0.5px] border-ios-sep bg-ios-bar px-[8px] py-[6px] shadow-[0_8px_30px_rgba(20,30,20,0.12)] backdrop-blur-2xl backdrop-saturate-[1.8]">
          <Composer conversationId={id} variant="mobile" />
        </div>
      )}
    </div>
  );
}
